import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, Radio } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { listGroupSources } from '../../services/cardDefs';
import { apiSlug } from '../../utils/conversions';
import { copiedElement, copyNameOf, groupLabelOf, rowFacts, rowKey } from '../../utils/groupCopy';

/**
 * "Copy a group from…" (KPR-859; keepr-api utils/groupCopy.js): the card
 * editor's door to a repeating group another card of this collection defines,
 * or one a starter installed in it supplied. The rulings (KPE-027, 2026-10-16):
 * a copy from an installed starter stays tied to the starter so its updates
 * can reach it, a copy from another card is plain, and a name the card already
 * has is asked for again here — never renamed behind the person's back.
 *
 * Fetches when it opens (`GET /api/collections/{id}/group-sources`), drawn
 * with DialogLoadState. Two sections, each a radio list: "This collection's
 * cards" (card › group) and "Installed starters" (starter › card › group),
 * each row with its columns' count and limits. Picking a row whose name is
 * already in use (`namesInUse`: this card's draft, the cards it extends, its
 * element sets, every card beneath it) asks for a new name, checked as it is
 * typed. **Copy group** hands the element to `onCopy` — the draft, marked;
 * nothing is written until the card is saved (utils/groupCopy.js).
 *
 * Mounted only while open (the caller's choice).
 */
export default function CopyGroupModal({ open, collectionId, card = null, namesInUse = new Set(), onCopy, onClose }) {
	const [rows, setRows] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [attempt, setAttempt] = useState(0);
	const [selected, setSelected] = useState(null);
	const [name, setName] = useState('');

	useEffect(() => {
		if (!open || !collectionId) return undefined;
		let current = true;
		setLoading(true);
		setError(null);
		listGroupSources(collectionId)
			.then((sources) => { if (current) setRows(sources); })
			.catch((err) => {
				if (!current) return;
				setError(err?.response?.data?.message || 'The groups to copy could not be read.');
			})
			.finally(() => { if (current) setLoading(false); });
		return () => { current = false; };
	}, [open, collectionId, attempt]);

	// The card being edited is not a source of its own groups.
	const ownId = card?._id ? String(card._id) : null;
	const listed = useMemo(() => (rows || []).filter(row => !(row.kind === 'card' && ownId && row.card?.id === ownId)), [rows, ownId]);
	const fromCards = listed.filter(row => row.kind === 'card');
	const fromStarters = listed.filter(row => row.kind === 'starter');
	const row = listed.find(entry => rowKey(entry) === selected) || null;

	// A name already on the card: asked for again, checked as typed.
	const ownName = row ? copyNameOf(row) : '';
	const clash = !!row && namesInUse.has(ownName);
	const typedName = name.trim();
	const typedSlug = apiSlug(typedName);
	let nameProblem = null;
	if (clash) {
		if (!typedName) nameProblem = `This card already has an element called “${groupLabelOf(row)}”. Give the copy another name.`;
		else if (!typedSlug) nameProblem = 'Use at least one letter or number.';
		else if (namesInUse.has(typedSlug)) nameProblem = `This card already has an element called “${typedName}”. Choose another name.`;
	}
	const ready = !!row && !nameProblem;

	const pick = (key) => {
		setSelected(key);
		setName('');
	};
	const copy = useCallback(() => {
		if (!ready) return;
		onCopy(copiedElement(row, clash ? typedName : null), row);
	}, [ready, row, clash, typedName, onCopy]);

	const renderRow = entry => (
		<div key={rowKey(entry)} className='copy-group__item' data-testid={`copy-group-row-${rowKey(entry)}`}>
		<Radio value={rowKey(entry)} className='copy-group__row'>
			<span className='copy-group__path'>
				{entry.kind === 'starter' ? <span className='copy-group__starter'>{entry.starter?.name || 'Starter'}</span> : null}
				{entry.kind === 'starter' ? <span className='copy-group__sep' aria-hidden='true'>›</span> : null}
				<RecordBadge kind='card' record={entry.card} title={entry.card?.name || 'Card'} size='xs' />
				<span className='copy-group__sep' aria-hidden='true'>›</span>
				<span className='copy-group__group'>{groupLabelOf(entry)}</span>
			</span>
			<span className='meta-line copy-group__facts'>{rowFacts(entry)}</span>
			{entry.unresolved?.length ? (
				<span className='copy-group__note'>{`${entry.unresolved.length === 1 ? 'Its link column' : 'Its link columns'} ${entry.unresolved.map(c => `“${c}”`).join(', ')} will link to nothing until you choose a card: the starter's card is gone.`}</span>
			) : null}
		</Radio>
		</div>
	);

	return (
		<Modal
			open={open}
			title='Copy a group'
			onCancel={onClose}
			onOk={copy}
			okText='Copy group'
			okButtonProps={{ disabled: !ready, 'data-testid': 'copy-group-ok' }}
			cancelText='Cancel'
			destroyOnClose
			className='copy-group-modal'
		>
			{card ? (
				<div className='modal-identity'>
					<RecordIdentity kicker='Card' badge={<RecordBadge kind='card' record={card} title={card.name || 'New card'} />} />
				</div>
			) : null}
			<p className='copy-group__lead'>
				Copies a group&rsquo;s columns, limits and headings onto this card. Nothing is saved until you save the card.
			</p>
			<DialogLoadState
				loaded={rows !== null}
				loading={loading}
				error={!!error}
				errorDetail={error || undefined}
				label='the groups to copy'
				onRetry={() => setAttempt(n => n + 1)}
				isEmpty={rows !== null && !listed.length}
				empty={<p className='copy-group__empty' data-testid='copy-group-empty'>No other card here has a repeating group yet: add one with Add element, or add a starter from Discover.</p>}
				skeleton={<Skeleton variant='list-row' count={4} />}
				minHeight={160}
			>
				<Radio.Group value={selected} onChange={e => pick(e.target.value)} className='copy-group__choices'>
					{fromCards.length ? (
						<section className='copy-group__section' aria-label="This collection's cards">
							<div className='micro-label copy-group__kicker'>This collection&rsquo;s cards</div>
							{fromCards.map(renderRow)}
						</section>
					) : null}
					{fromStarters.length ? (
						<section className='copy-group__section' aria-label='Installed starters'>
							<div className='micro-label copy-group__kicker'>Installed starters</div>
							<p className='copy-group__hint'>A copy from a starter stays tied to it, so its updates can reach the copy.</p>
							{fromStarters.map(renderRow)}
						</section>
					) : null}
				</Radio.Group>
			</DialogLoadState>
			{clash ? (
				<Form layout='vertical' className='copy-group__name' onFinish={copy}>
					<Form.Item
						label='Name'
						htmlFor='copy-group-name'
						validateStatus={typedName && nameProblem ? 'error' : undefined}
						help={nameProblem || `Saved as “${typedName}”.`}
					>
						<Input
							id='copy-group-name'
							value={name}
							onChange={e => setName(e.target.value)}
							onPressEnter={copy}
							autoFocus
							data-testid='copy-group-name'
						/>
					</Form.Item>
				</Form>
			) : null}
		</Modal>
	);
}
